import React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ConfigReportesDefiniciones.scss';
import { useApiConfiguration } from '../../api/api.configuration';
import Button from 'devextreme-react/button';
import { CheckBox, Tooltip } from 'devextreme-react';
import DataGrid, { Column, Button as Butt, Editing, Item as Items, Paging, Toolbar, Popup, Lookup, RequiredRule, DataGridTypes, Selection, Form, SearchPanel, Scrolling } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { GroupItem, Item } from 'devextreme-react/form';
import { useScreenSize } from '../../utils/media-query';
import { Cast, SystemFields, Tablas, TablasCombobox, TipoCampo, Types } from '../../types/types.config';
import { get } from 'http';
import { Tab } from 'devextreme-react/diagram';
import { useAppSelector } from '../../contexts/hooks';
import { ModalDefiniciones } from './Modals/ModalDefiniciones';
import notify from 'devextreme/ui/notify';

const Index = () => {
  const { isMedium, isSmall } = useScreenSize();
  const { getDefiniciones, newDefiniciones, setDefiniciones, deleteDefinicion } = useApiConfiguration();
  const [data, setData] = useState<SystemFields[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [idDefinicion, setIdDefinicion] = useState(-1);
  const dataGridRef = useRef<DataGrid>(null);
  const lstComboBox = useAppSelector((x) => x.configuration.Data?.ComboBox);

  // Extraer solo id y nombre
  const comboBoxData =
    lstComboBox?.map((item) => ({
      id: item.Id,
      nombre: item.Name,
    })) || [];

  useEffect(() => {
    getDefiniciones().then((res) => {
      setData(res);
    });
  }, []);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Dfc_Id',
      loadMode: 'processed',
      load() {
        return getDefiniciones()
          .then((res) => ({
            data: res,
            totalCount: res.totalCount,
            summary: res.summary,
            groupCount: res.groupCount,
          }))
          .catch(() => {
            throw new Error('Error de carga de datos');
          });
      },
      insert: async (values) => {
        return newDefiniciones({ Definicion: values })
          .then((res) => {
            if (res === 105) {
              return getDefiniciones().then((res) => setData(res));
            }
          })
          .catch(() => {
            throw new Error('Error insertando la definición');
          });
      },
      update: (key, values) => {
        const currentItem = data.find((item) => item.Dfc_Id === key);

        if (!currentItem) {
          throw new Error('No se encontró la definición para actualizar');
        }

        const updatedItem = { ...currentItem, ...values };

        return setDefiniciones({ Definicion: updatedItem, IdDefinicion: key })
          .then((res) => {
            if (res === 107) {
              return getDefiniciones().then(setData);
            }
          })
          .catch(() => {
            throw new Error('Error actualizando la definición');
          });
      },

      remove: (key) => {
        return deleteDefinicion({ Id: key }).then((res) => {
          if (res === 107) {
            return getDefiniciones().then(setData);
          }
        });
      },
    });
  }, [data]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleDetails = useCallback(
    (e) => {
      dataGridRef.current?.instance.refresh();
      const { data }: { data: SystemFields } = e.row;
      setIdDefinicion(data.Dfc_Id);
      setIsModalVisible(true);
    },
    [idDefinicion]
  );

  const handleSelection = useCallback(
    ({ selectedRowsData }: DataGridTypes.SelectionChangedEvent) => {
      const dataGrid = selectedRowsData[0];
      setSelectedRow(dataGrid);
    },
    [setSelectedRow]
  );

  const handleUpdateData = useCallback(() => {
    getDefiniciones().then((res) => {
      setData(res);
    });
  }, [setData]);

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid
          className='grid'
          dataSource={customDataSrc}
          height='100%'
          repaintChangesOnly={true}
          highlightChanges={true}
          rowAlternationEnabled={true}
          showColumnLines={true}
          onSelectionChanged={handleSelection}
          onInitNewRow={(e) => {
            e.data.Dfc_Estado = true;
            e.data.Dfc_Requerido = true;
            e.data.Dfc_Editable = true;
            e.data.Dfc_Dinamico = true;
            e.data.Dfc_Visible = true;
          }}
          onRowRemoving={(e) => {
            if (e.data.Dfc_Dinamico === false) {
              e.cancel = true;
              notify('No se puede eliminar una fila con Dinámico en falso', 'warning', 2000);
            }
          }}
          onRowValidating={(e) => {
            const selectedType = e.newData?.Dfc_Tcm_Id || e.oldData?.Dfc_Tcm_Id;
            const isCheckbox = selectedType === 3;

            if (!isCheckbox) {
              const comboBoxValue = e.newData?.Dfc_Tbc_Id ?? e.oldData?.Dfc_Tbc_Id;
              if (!comboBoxValue) {
                e.isValid = false;
                e.errorText = 'El campo combo box es obligatorio';
              }
            }

            const isNumberBox = selectedType === 9;

            if (isNumberBox) {
              if (!e.newData?.Dfc_Minimo) {
                e.isValid = false;
                e.errorText = 'El campo mínimo es obligatorio';
              }
              if (!e.newData?.Dfc_Maximo) {
                e.isValid = false;
                e.errorText = 'El campo máximo es obligatorio';
              }
            }
          }}>
          <SearchPanel visible={true} />
          <Scrolling columnRenderingMode='standard' mode='virtual' />
          <Editing mode='popup' allowAdding allowDeleting allowUpdating texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
            <Popup title='INFORMACIÓN DEFINICIONES' width={1200} height={'90%'} fullScreen={isMedium || isSmall} showTitle={true} />
          </Editing>
          <Paging enabled={false} />
          <Selection mode='single' />
          <Toolbar>
            <Items location='before'>
              <div className='grid-header'>Definiciones de campos</div>
            </Items>
            <Item name='searchPanel' />
            <Item name='addRowButton' />
            <Items location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RefrecarResult' icon='refresh' text='Refresh' stylingMode='text' onClick={handleUpdateData} />
              <Tooltip target={'#RefrecarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Items>
          </Toolbar>
          <Column caption='ID' allowEditing={false} visible={false} dataField='Dfc_Id' alignment='center' width={50} />
          <Column allowGrouping={false} caption='TIPO' visible={true} allowEditing={true} dataField='Dfc_Tcm_Id'>
            <Lookup dataSource={Types} valueExpr='id' displayExpr='name' />
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='COMBO BOX' visible={false} allowEditing={true} dataField='Dfc_Tbc_Id'>
            <Lookup dataSource={comboBoxData} valueExpr='id' displayExpr='nombre' />
          </Column>
          <Column allowGrouping={false} caption='LABEL' visible={true} allowEditing={true} dataField='Dfc_Label'>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='TABLA' visible={true} allowEditing={true} dataField='Dfc_Tabla'>
            <Lookup dataSource={Tablas} valueExpr='name' displayExpr='name' />
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='EQUIVALENCIA' visible={true} allowEditing={true} dataField='Dfc_Equivalencia'>
            <RequiredRule />
          </Column>
          <Column caption='REQUERIDO' allowEditing={true} visible={true} dataField='Dfc_Requerido' alignment='center' width={90}></Column>
          <Column caption='EDITABLE' allowEditing={true} visible={false} dataField='Dfc_Editable' alignment='center' width={90}></Column>
          <Column allowGrouping={false} caption='MINIMO' visible={true} allowEditing={true} dataField='Dfc_Minimo'></Column>
          <Column allowGrouping={false} caption='MAXIMO' visible={true} allowEditing={true} dataField='Dfc_Maximo'></Column>
          <Column allowGrouping={false} caption='ORDEN' visible={true} allowEditing={true} dataField='Dfc_Orden'>
            <RequiredRule />
          </Column>
          <Column allowGrouping={false} caption='NRO COLUMNAS' visible={false} allowEditing={true} dataField='Dfc_NroColumnas'>
            <RequiredRule />
          </Column>
          <Column caption='DINAMICO' allowEditing={true} visible={true} dataField='Dfc_Dinamico' alignment='center' width={90}></Column>
          <Column allowGrouping={false} caption='MASCARA' visible={false} allowEditing={true} dataField='Dfc_Mascara'></Column>
          <Column caption='ESTADO' allowEditing={true} visible={false} dataField='Dfc_Estado' alignment='center' width={90}></Column>
          <Column allowGrouping={false} allowSorting={false} caption='CAST' allowEditing={true} dataField='Dfc_Cast' alignment='center' width={90}>
            <Lookup dataSource={Cast} valueExpr='name' displayExpr='name' />
          </Column>
          {/* <Column allowGrouping={false} caption='DEPENDENCIA' visible={false} allowEditing={true} dataField='Dfc_Dependency'></Column> */}
          <Column allowGrouping={false} caption='DEPENDENCIA' visible={false} allowEditing={true} dataField='Dfc_Dependency'>
            <Lookup dataSource={comboBoxData} valueExpr='id' displayExpr='nombre' />
          </Column>
          <Column allowGrouping={false} caption='DEPENDENCIA CAMPO' visible={false} allowEditing={true} dataField='Dfc_DependencyField'></Column>
          <Column allowGrouping={false} caption='DEPENDENCIA DEFINICION' visible={false} allowEditing={true} dataField='Dfc_DependencyDefinition'></Column>
          <Column caption='VISIBLE' allowEditing={true} visible={true} dataField='Dfc_Visible' alignment='center' width={90}></Column>
          <Column type='buttons' caption={'ACCIÓN'} width={150}>
            <Butt name='edit' />
            <Butt name='delete' />
            <Butt hint='Configurar' icon='eyeopen' visible={true} disabled={false} onClick={handleDetails} />
          </Column>
          {isModalVisible && <ModalDefiniciones visible={isModalVisible} setVisible={() => setIsModalVisible(false)} Id={idDefinicion} />}
        </DataGrid>
      </div>
    </div>
  );
};

export default Index;
